import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ImpressoraDTO } from '../../../models/impressora.models';
import { TableColumn } from '../../../models/shared/table-column.model';
import { ImpressoraService } from '../../../services/impressora/impressora.service';

/**
 * Gestao de impressoras da empresa. Segue o padrao visual do `timbrado`: cabecalho de pagina,
 * card de lista com busca e seletor de colunas, colunas ordenaveis e paginador proprio.
 *
 * Escopo: ADICIONAR e EXCLUIR. A edicao existe no backend (`PUT /impressora/{id}`) e no dialog
 * (`[impressora]`), mas foi deixada de fora da tela por decisao de produto.
 *
 * Paginacao, ordenacao e busca sao feitas em MEMORIA: o volume de impressoras por empresa e de
 * poucas dezenas, e o endpoint de filtro do backend (`/impressora/filtrarImpressora`) lanca
 * excecao quando o resultado e vazio, o que quebraria a busca sem correspondencia.
 */
@Component({
  selector: 'app-impressoras',
  templateUrl: './impressoras.component.html',
  styleUrls: ['./impressoras.component.scss'],
})
export class ImpressorasComponent implements OnInit, OnDestroy {

  readonly STORAGE_KEY = 'impressoras-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'impressoras.labels.id', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigo', labelKey: 'impressoras.labels.codigo', visible: true, fixed: false, defaultVisible: true },
    { key: 'nome', labelKey: 'impressoras.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'caminho', labelKey: 'impressoras.labels.caminho', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'impressoras.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  impressoras: ImpressoraDTO[] = [];
  loading = true;
  filterName = '';

  // Paginacao (client-side)
  first = 0;
  rows = 10;
  private _lastRows = 10;
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Ordenacao (client-side)
  sortField = 'nome';
  sortOrder: 1 | -1 = 1;

  dialogVisible = false;

  deleteDialogVisible = false;
  impressoraParaExcluir: ImpressoraDTO | null = null;
  excluindo = false;

  texts = {
    titulo: '', subtitulo: '', lista: '', nova: '', nenhuma: '', ajuste_filtros: '',
    confirmacao: '', confirmacao_excluir: '',
  };
  labels = { id: '', codigo: '', nome: '', caminho: '', acoes: '', itens_por_pagina: '' };
  placeholders = { pesquisar: '' };
  tooltips = { excluir: '' };
  botoes = { cancelar: '', confirmar: '' };

  private langSub?: Subscription;

  constructor(
    private impressoraService: ImpressoraService,
    private translate: TranslateService,
    private messageService: MessageService,
  ) { }

  ngOnInit(): void {
    this.carregarTraducoes();
    this.langSub = this.translate.onLangChange.subscribe(() => this.carregarTraducoes());
    this.carregar();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /** Lista completa apos busca e ordenacao — base da contagem do paginador. */
  get impressorasFiltradas(): ImpressoraDTO[] {
    const termo = this.filterName.trim().toLowerCase();
    const filtradas = !termo ? [...this.impressoras] : this.impressoras.filter(i =>
      (i.nome || '').toLowerCase().includes(termo) ||
      (i.caminho || '').toLowerCase().includes(termo) ||
      String(i.codigoImpressora ?? '').includes(termo)
    );

    return filtradas.sort((a, b) => {
      const va = this.valorDaColuna(a, this.sortField);
      const vb = this.valorDaColuna(b, this.sortField);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * this.sortOrder;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * this.sortOrder;
    });
  }

  /** Fatia exibida na pagina atual. */
  get impressorasDaPagina(): ImpressoraDTO[] {
    return this.impressorasFiltradas.slice(this.first, this.first + this.rows);
  }

  get totalRecords(): number {
    return this.impressorasFiltradas.length;
  }

  carregar(): void {
    this.loading = true;
    this.impressoraService.get('/impressora', { page: 0, size: 100, sort: 'nome,asc' }).subscribe({
      next: (res: any) => {
        this.impressoras = Array.isArray(res) ? res : (res?.content || []);
        this.loading = false;
      },
      error: () => {
        this.impressoras = [];
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('impressoras.mensagens.erro_carregar'),
        });
      },
    });
  }

  onSearchChange(valor: string): void {
    this.filterName = valor || '';
    this.first = 0;
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
  }

  /**
   * Ordenacao em memoria. Usa `[customSort]` para o p-table nao reordenar por conta propria —
   * quem ordena e o getter `impressorasFiltradas`, antes do fatiamento da pagina.
   */
  customSort(event: SortEvent): void {
    if (!event.field) return;
    this.sortField = event.field;
    this.sortOrder = (event.order ?? 1) === 1 ? 1 : -1;
    this.first = 0;
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  novaImpressora(): void {
    this.dialogVisible = true;
  }

  onImpressoraCriada(): void {
    this.carregar();
  }

  confirmarExclusao(impressora: ImpressoraDTO): void {
    this.impressoraParaExcluir = impressora;
    this.deleteDialogVisible = true;
  }

  excluir(): void {
    if (!this.impressoraParaExcluir?.id) return;
    this.excluindo = true;

    this.impressoraService.delete(`/impressora/${this.impressoraParaExcluir.id}`).subscribe({
      next: () => {
        this.excluindo = false;
        this.deleteDialogVisible = false;
        this.impressoraParaExcluir = null;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('impressoras.mensagens.impressora_excluida'),
        });
        this.first = 0;
        this.carregar();
      },
      error: (err) => {
        this.excluindo = false;
        this.deleteDialogVisible = false;
        // Impressora referenciada por item ou pela configuracao da empresa: as 4 FKs sao
        // `NO ACTION` e o backend devolve 400 com `registro_em_uso`, ja traduzido. Preferimos a
        // mensagem dele e so caimos no texto proprio se ela faltar.
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('impressoras.mensagens.erro_excluir'),
        });
      },
    });
  }

  private valorDaColuna(impressora: ImpressoraDTO, campo: string): string | number {
    switch (campo) {
      case 'id': return impressora.id ?? 0;
      case 'codigo': return impressora.codigoImpressora ?? 0;
      case 'caminho': return impressora.caminho || '';
      default: return impressora.nome || '';
    }
  }

  private carregarTraducoes(): void {
    const p = 'impressoras';
    this.texts = {
      titulo: this.translate.instant(`${p}.texts.titulo`),
      subtitulo: this.translate.instant(`${p}.texts.subtitulo`),
      lista: this.translate.instant(`${p}.texts.lista_de_impressoras`),
      nova: this.translate.instant(`${p}.texts.nova_impressora`),
      nenhuma: this.translate.instant(`${p}.texts.nenhuma_impressora`),
      ajuste_filtros: this.translate.instant(`${p}.texts.ajuste_filtros`),
      confirmacao: this.translate.instant(`${p}.texts.confirmacao`),
      confirmacao_excluir: this.translate.instant(`${p}.texts.confirmacao_excluir`),
    };
    this.labels = {
      id: this.translate.instant(`${p}.labels.id`),
      codigo: this.translate.instant(`${p}.labels.codigo`),
      nome: this.translate.instant(`${p}.labels.nome`),
      caminho: this.translate.instant(`${p}.labels.caminho`),
      acoes: this.translate.instant(`${p}.labels.acoes`),
      itens_por_pagina: this.translate.instant('timbrado.labels.itens_por_pagina'),
    };
    this.placeholders = { pesquisar: this.translate.instant(`${p}.placeholders.pesquisar`) };
    this.tooltips = { excluir: this.translate.instant(`${p}.tooltips.excluir`) };
    this.botoes = {
      cancelar: this.translate.instant('cancelar'),
      confirmar: this.translate.instant('common.confirmar'),
    };
  }
}
